import ApiClient from "@/domain/core/utils/apiClient";
import client_gateway from "@/services/clients/client_gateway";
import client_core from "@/services/clients/client_core";
import PrintJobStatus from "@/domain/core/constant/printJobStatus";
import downloader from "@/domain/core/utils/downloader";

const gatewayClient = new ApiClient(client_gateway)
const coreClient = new ApiClient(client_core)

const endpoints = {
    // Ecommerce endpoints
    orders: '/dashboard/ecommerce/orders/',
    orderDetail: '/dashboard/ecommerce/orders/{id}/',
    orderPatch: '/dashboard/ecommerce/orders/{id}/',

    // Print endpoints
    printJobs: '/dashboard/print/print-jobs/',
    printJobDetail: '/dashboard/print/print-jobs/{id}/',
    printJobCreate: '/dashboard/print/print-jobs/',

    // Edge orders media endpoint (Core)
    edgeOrderMedia: '/dashboard/v2/edge-orders/{picaServiceId}/get_media/',

    // Media download endpoint (Core)
    downloadMedia: '/dashboard/v2/edge-orders/{picaServiceId}/download_media/',

    // Photo search endpoints (Core) - for soft deleting (hiding) media
    softDeleteMedia: '/dashboard/v2/photo-search/{picaServiceId}/soft_delete/',

    // Unlink picacode endpoint (Core) - for removing picacode from photos
    unlinkPicaCode: '/dashboard/v2/photo-search/{picaServiceId}/unlink_pica_code/'
}

export default {
    // Orders
    async fetchOrders(picaServiceId, params) {
        params = { ...params, pica_service_id: picaServiceId }

        // Process date range filters
        if (params.created_at_range) {
            const range = params.created_at_range
            if (range.start) params.created_at__gte = range.start
            if (range.end) params.created_at__lte = range.end
            delete params.created_at_range
        }

        if (params.paid_at_range) {
            const range = params.paid_at_range
            if (range.start) params.paid_at__gte = range.start
            if (range.end) params.paid_at__lte = range.end
            delete params.paid_at_range
        }

        // Remove null/empty values
        const cleanParams = Object.keys(params).reduce((acc, key) => {
            if (params[key] !== null && params[key] !== '') {
                acc[key] = params[key]
            }
            return acc
        }, {})

        return await gatewayClient.get(endpoints.orders, cleanParams)
    },

    async fetchOrderDetail(orderId) {
        const endpoint = endpoints.orderDetail.replace('{id}', orderId)
        return await gatewayClient.get(endpoint)
    },

    async updateOrderStatus(orderId, status) {
        const endpoint = endpoints.orderPatch.replace('{id}', orderId)
        return await gatewayClient.patch(endpoint, { status })
    },

    // Print jobs
    async fetchPrintJobs(params = {}) {
        // Process params
        // Supports pagination via query params: page, page_size
        // Example: { page: 1, page_size: 20, order_id: 123 }
        const cleanParams = { ...params }

        // Convert hours to created_at__gte
        if (cleanParams.hours) {
            const hours = cleanParams.hours
            delete cleanParams.hours
            const now = new Date()
            const hoursAgo = new Date(now.getTime() - (hours * 60 * 60 * 1000))
            cleanParams.created_at__gte = hoursAgo.toISOString()
        }

        // Convert exclude_completed to status__not
        const excludeCompleted = cleanParams.exclude_completed
        if (cleanParams.exclude_completed !== undefined) {
            delete cleanParams.exclude_completed
            if (excludeCompleted) {
                cleanParams.status__not = PrintJobStatus.COMPLETED.value
            }
        }

        // API returns array directly: [{...}, {...}]
        return await gatewayClient.get(endpoints.printJobs, cleanParams)
    },

    async fetchPrintJobDetail(printJobId) {
        const endpoint = endpoints.printJobDetail.replace('{id}', printJobId)
        return await gatewayClient.get(endpoint)
    },

    async createPrintJobs(items) {
        return await gatewayClient.post(endpoints.printJobCreate, { items })
    },

    // Media from edge orders endpoint (Core)
    async fetchEdgeOrderMedia(picaServiceId, params, queryParams = {}) {
        const endpoint = endpoints.edgeOrderMedia.replace('{picaServiceId}', picaServiceId)

        // Build query string for pagination
        const queryString = new URLSearchParams({
            page: queryParams.page || 1,
            page_size: queryParams.page_size || 100  // Default to 100 for better performance
        }).toString()

        // POST request with picacode and optionally media_ids or gallery_ids
        // Include printable parameter if provided (for watermark control)
        const requestParams = { ...params }
        if (queryParams.printable !== undefined) {
            requestParams.printable = queryParams.printable
        }

        const response = await coreClient.post(`${endpoint}?${queryString}`, requestParams)

        // Return response as-is, no need to remap fields
        // API already provides thumb and sd fields

        return response
    },

    // Download media endpoint (Core)
    async downloadMedia(picaServiceId, params) {
        const endpoint = endpoints.downloadMedia.replace('{picaServiceId}', picaServiceId)

        // Build request body with printable parameter if provided
        const requestParams = { ...params }

        // Request the download URL from the API
        const response = await coreClient.post(endpoint, requestParams)

        // API returns {status: 'success', url: 'presigned_s3_url'}
        if (response.status === 'success' && response.url) {
            // Download using the downloader utility
            // Add suffix to filename based on printable status
            const suffix = params.printable === true ? 'printable' : params.printable === false ? 'digital' : 'photos'
            downloader.downloadFromUrl(response.url, `${suffix}_${params.picacode}_${Date.now()}.zip`)
        } else {
            throw new Error('Failed to generate download URL')
        }

        return response
    },

    // Soft delete (hide) media (using photoSearch endpoint)
    async softDeleteMedia(picaServiceId, mediaIds) {
        const endpoint = endpoints.softDeleteMedia
            .replace('{picaServiceId}', picaServiceId)

        // Format selected parameter as array of filter objects with ids (as strings)
        // PhotoSearch expects ids as string array
        const selected = [{ ids: mediaIds.map(id => String(id)) }]

        return await coreClient.post(endpoint, {
            selected: selected,
            filters: {}  // Must be an object, not an array
        })
    },

    // Unlink picacode from photos
    async unlinkPicaCode(picaServiceId, mediaIds, picacode) {
        const endpoint = endpoints.unlinkPicaCode
            .replace('{picaServiceId}', picaServiceId)

        // Format selected parameter as array of filter objects with ids (as strings)
        const selected = [{ ids: mediaIds.map(id => String(id)) }]

        return await coreClient.post(endpoint, {
            selected: selected,
            pica_code: picacode
        })
    }
}